import { useEffect } from 'react';
import { Alert, App, Button, Card, Form, Input, InputNumber, Space, Statistic, Switch } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

export interface DailyBonusConfig {
  enabled: boolean;
  coins: number;
  title: string;
  message: string;
  buttonText: string;
  /** When the daily reminder push goes out (HH:mm, business timezone). */
  pushTime: string;
  /** Daily "go live" push to listeners — its own switch and time. */
  goLivePushEnabled: boolean;
  goLivePushTime: string;
}

/** 24-hour HH:mm, zero-padded — what the API accepts. */
export const PUSH_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** What the API returns: the settings plus read-only day info and today's totals. */
export interface DailyBonusConfigView extends DailyBonusConfig {
  timezone: string;
  businessDate: string;
  resetsAt: string;
  claimsToday: number;
  coinsToday: number;
}

/** Same limits the API enforces. */
export const DAILY_BONUS_MAX_COINS = 10_000;
export const DAILY_BONUS_TITLE_MAX = 60;
export const DAILY_BONUS_MESSAGE_MAX = 160;
export const DAILY_BONUS_BUTTON_MAX = 24;

export const DAILY_BONUS_KEY = ['admin', 'daily-bonus', 'config'];

/** Exactly the fields the API accepts — it rejects anything extra. */
export function dailyBonusBody(v: DailyBonusConfig): DailyBonusConfig {
  return {
    enabled: v.enabled === true,
    coins: v.coins,
    title: v.title.trim(),
    message: v.message.trim(),
    buttonText: v.buttonText.trim(),
    pushTime: v.pushTime.trim(),
    goLivePushEnabled: v.goLivePushEnabled === true,
    goLivePushTime: v.goLivePushTime.trim(),
  };
}

/**
 * Daily Welcome Bonus — free coins a normal user can claim once a day when they
 * open the app. Applies to the next claim; past claims keep what they paid.
 */
export function DailyWelcomeBonusCard() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<DailyBonusConfig>();

  const cfg = useQuery({
    queryKey: DAILY_BONUS_KEY,
    queryFn: async () => (await api.get<DailyBonusConfigView>('/admin/daily-bonus/config')).data,
  });

  useEffect(() => {
    if (cfg.data) form.setFieldsValue(dailyBonusBody(cfg.data));
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (v: DailyBonusConfig) =>
      (await api.put<DailyBonusConfigView>('/admin/daily-bonus/config', dailyBonusBody(v))).data,
    onSuccess: () => {
      message.success('Daily Welcome Bonus saved — applies from the next claim.');
      void qc.invalidateQueries({ queryKey: DAILY_BONUS_KEY });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed',
      ),
  });

  const required = (label: string) => ({ required: true, whitespace: true, message: `${label} is required` });

  return (
    <Card title="Daily Welcome Bonus" loading={cfg.isLoading} style={{ maxWidth: 700 }}>
      {cfg.isError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Could not load the Daily Welcome Bonus settings." />
      )}
      <Form<DailyBonusConfig> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item
          name="enabled"
          label="Status"
          valuePropName="checked"
          tooltip="ON: normal users see the bonus when they open the app and can claim it once a day. Listeners never get it."
        >
          <Switch checkedChildren="ON" unCheckedChildren="OFF" aria-label="Daily Welcome Bonus status" />
        </Form.Item>
        <Form.Item
          name="coins"
          label="Bonus coins"
          tooltip="Coins added to the user's wallet per claim."
          rules={[
            {
              required: true,
              type: 'integer',
              min: 1,
              max: DAILY_BONUS_MAX_COINS,
              message: `Enter a whole number from 1 to ${DAILY_BONUS_MAX_COINS}`,
            },
          ]}
        >
          <InputNumber min={1} max={DAILY_BONUS_MAX_COINS} precision={0} aria-label="Bonus coins" />
        </Form.Item>
        <Form.Item name="title" label="Title" rules={[required('Title')]}>
          <Input maxLength={DAILY_BONUS_TITLE_MAX} showCount placeholder="Welcome Back!" />
        </Form.Item>
        <Form.Item name="message" label="Message" rules={[required('Message')]}>
          <Input maxLength={DAILY_BONUS_MESSAGE_MAX} showCount placeholder="Claim your daily welcome bonus." />
        </Form.Item>
        <Form.Item name="buttonText" label="Button text" rules={[required('Button text')]}>
          <Input maxLength={DAILY_BONUS_BUTTON_MAX} showCount placeholder="Claim Coins" />
        </Form.Item>
        <Form.Item
          name="pushTime"
          label="Daily reminder push time"
          tooltip='While the bonus is ON, every user who has not claimed yet gets a "Daily Bonus Unlocked" push at this time each day.'
          rules={[{ required: true, pattern: PUSH_TIME_RE, message: 'Use 24-hour HH:mm, e.g. 10:00' }]}
        >
          <Input style={{ width: 120 }} placeholder="10:00" aria-label="Daily reminder push time" />
        </Form.Item>
        <Form.Item
          name="goLivePushEnabled"
          label='Listener "go live" reminder'
          valuePropName="checked"
          tooltip="ON: approved listeners who are OFFLINE get a daily push reminding them to go online. Independent of the bonus switch."
        >
          <Switch checkedChildren="ON" unCheckedChildren="OFF" aria-label="Listener go live reminder" />
        </Form.Item>
        <Form.Item
          name="goLivePushTime"
          label='"Go live" push time'
          rules={[{ required: true, pattern: PUSH_TIME_RE, message: 'Use 24-hour HH:mm, e.g. 18:00' }]}
        >
          <Input style={{ width: 120 }} placeholder="18:00" aria-label="Go live push time" />
        </Form.Item>

        {cfg.data && (
          <>
            <Space size="large" style={{ marginBottom: 12 }}>
              <Statistic title={`Claims today (${cfg.data.businessDate})`} value={cfg.data.claimsToday} />
              <Statistic title="Coins given today" value={cfg.data.coinsToday} />
            </Space>
            <Alert
              type="info"
              showIcon
              style={{ margin: '0 0 16px' }}
              message={
                `Every eligible user can claim every day. One claim per user per day. The day resets at midnight ${cfg.data.timezone} time. Each claim appears in the user's wallet history as "Daily Welcome Bonus". Changes apply within about a minute.`
              }
            />
          </>
        )}
        <Can permission="offers.edit">
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            Save Daily Welcome Bonus
          </Button>
        </Can>
      </Form>
    </Card>
  );
}
